/**
 * Why does this exist?
 *
 * https://github.com/davidcetinkaya/embla-carousel/issues/212
 *
 * tl;dr We can't use the package in a yarn pnp enabled project.
 *
 */
import { useRef, useEffect, useState, useMemo } from 'react';
import EmblaCarousel from 'embla-carousel';
import type { EmblaOptionsType, EmblaCarouselType } from 'embla-carousel';

function canUseDOM(): boolean {
  return !!(
    typeof window !== 'undefined' &&
    window.document &&
    window.document.createElement
  );
}

function areEqualShallow(
  objectA: Record<string, unknown>,
  objectB: Record<string, unknown>
): boolean {
  return (
    Object.keys(objectA).length === Object.keys(objectB).length &&
    Object.keys(objectA).every((objectKey) => {
      if (!Object.prototype.hasOwnProperty.call(objectB, objectKey)) {
        return false;
      }
      return objectA[objectKey] === objectB[objectKey];
    })
  );
}

type ViewportRefType = <ViewportElement extends HTMLElement>(
  instance: ViewportElement | null
) => void;

export type UseEmblaCarouselType = [
  ViewportRefType,
  EmblaCarouselType | undefined,
];

type EmblaCarouselOptionsType = EmblaOptionsType & {
  showArrows?: boolean;
};

function useEmblaCarousel(
  options: EmblaCarouselOptionsType = {}
): UseEmblaCarouselType {
  const [embla, setEmbla] = useState<EmblaCarouselType>();
  const [viewport, setViewport] = useState<HTMLElement>();
  const storedOptions = useRef<EmblaOptionsType>(options);
  const activeOptions = useMemo<EmblaOptionsType>(() => {
    if (!areEqualShallow(storedOptions.current, options)) {
      storedOptions.current = options;
    }
    return storedOptions.current;
  }, [storedOptions, options]);

  useEffect(() => {
    if (canUseDOM() && viewport) {
      const newEmbla = EmblaCarousel(viewport, activeOptions);
      setEmbla(newEmbla);
      return () => newEmbla.destroy();
    } else {
      setEmbla(undefined);
    }
  }, [viewport, activeOptions, setEmbla]);

  return [setViewport as ViewportRefType, embla];
}

export { useEmblaCarousel };
